/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {setAssignedAssetsPanels} from '@/store/actions/tracking';
import {assignedAssetsPanelsOpenSelector} from '@/store/selectors/tracking-selectors';

/**
 * useToggleAssignedAssetsPanel Hook
 *
 * A custom hook that provides the `onToggleAssignedAssetsPanel` function
 * for toggling the visibility of assigned assets panels.
 *
 * @returns {Object} An object containing the `onToggleAssignedAssetsPanel` function.
 */
const useToggleAssignedAssetsPanel = () => {
  const dispatch = useDispatch();
  const assignedAssetsPanelsOpen = useSelector(assignedAssetsPanelsOpenSelector);

  /**
   * Toggle the visibility of the assigned assets panel.
   *
   * @param {string} id - The ID of the assigned assets panel to toggle.
   */
  const onToggleAssignedAssetsPanel = useCallback((id) => {
    if (assignedAssetsPanelsOpen === id) {
      dispatch(setAssignedAssetsPanels());
    } else {
      dispatch(setAssignedAssetsPanels(id));
    }
  }, [setAssignedAssetsPanels, assignedAssetsPanelsOpen]);

  return {
    onToggleAssignedAssetsPanel,
    assignedAssetsPanelsOpen
  };
};

export default useToggleAssignedAssetsPanel;
